import React, { useState, useEffect, useRef } from 'react'
import { useCombobox } from 'downshift'
import { lit, buildClassName } from 'src/utils'
import { usePrevious } from 'src/hooks'
import { SearchIcon } from '../../icons/SearchIcon'

export interface AutoSuggestProps<THint = any> {
    className?: string

    ulClassName?: string
    /**
     * Placeholder text for the input box
     */
    placeholder?: string
    /**
     * Accessibility label for the input box
     */
    ariaLabel?: string
    /**
     * Minimum number of characters the user types before hints start appearing
     */
    minInputLength?: number

    headerHints?: THint[]
    /**
     * Array of search hints to be displayed based on the current search input
     */
    hints: THint[]
    /**
     * Text to be displayed when no hints are returned
     */
    noResultsText?: string
    /**
     * Class name to be applied to the input box
     */
    inputBorderClassName?: string
    /**
     * Additional class names for the input element
     */
    inputClassName?: string
    /**
     * Position of the search icon
     * */
    searchIconPosition?: 'left' | 'right'
    /**
     * Class name to be applied to the listbox
     */
    listboxClassName?: string
    /**
     * Class name to be applied to hint padding in the list
     * */
    hintPaddingClassName?: string

    /**
     * Optional content to be rendered in the input
     * */
    inputAside?: React.ReactNode
    /**
     * If true, the input will be focused on mount
     * */
    autoFocus?: boolean
    /**
     * Opens menu upon focus
     * */
    openMenuOnFocus?: boolean
    /**
     * Callback function to fetch search hints and update the `hints` prop
     */
    fetchHints: (keywords: string) => void
    /**
     * Renders the selected hint as a simple string so it can be shown in the input box
     */
    hintToString?: (hint: THint | null) => string
    /**
     * Optional callback to be called when the user selects a hint from the list
     */
    onSelectHint?: (selectedHint: THint, userEnteredString: string) => void
    /**
     * Optional callback for when user presses enter
     */
    onEnterKeyPressed?: (inputValue?: string) => void
    /**
     * A render-prop function that renders each item in the list of hints
     */
    children?: (item: THint) => React.ReactNode
    /**
     * Ref for the input element. Note: forwardRef does not work with generic types hence this prop
     */
    inputRef?: React.Ref<HTMLInputElement>
    /**
     * Optional prop used for rare edge cases to set inline styles for the ul element
     */
    ulStyle?: Record<string, string>
}

export function AutoSuggest<THint = any>({
    className,
    inputClassName,
    ulClassName,
    placeholder,
    ariaLabel,
    minInputLength = 1,
    hints,
    noResultsText,
    inputBorderClassName = 'border border-black',
    searchIconPosition = 'right',
    listboxClassName = '',
    hintPaddingClassName = 'py-2 px-4',
    headerHints,
    inputAside,
    autoFocus = false,
    openMenuOnFocus = false,
    fetchHints,
    hintToString,
    onSelectHint,
    onEnterKeyPressed,
    inputRef,
    ulStyle,
    children: renderHint = defaultRenderHintFunc,
}: AutoSuggestProps<THint>) {
    if (!ariaLabel) {
        ariaLabel = placeholder
    }

    const [adjustedListBoxHeight, setAdjustedListBoxHeight] = useState(0)
    const listBoxRef = useRef<HTMLUListElement>(null)
    const prevInputValueRef = useRef('')

    const {
        isOpen,
        getInputProps,
        highlightedIndex,
        getItemProps,
        inputValue,
        openMenu,
        getMenuProps,
    } = useCombobox({
        items: [...(headerHints ?? []), ...hints],
        itemToString: hintToString as (hint: THint | null) => string,
        onStateChange({ type }) {
            if (type === useCombobox.stateChangeTypes.InputKeyDownEnter) {
                onEnterKeyPressed?.(inputValue)
            }
        },
        onInputValueChange: ({ inputValue, type }) => {
            if (
                type === useCombobox.stateChangeTypes.ItemClick ||
                type === useCombobox.stateChangeTypes.InputKeyDownEnter
            ) {
                return
            }

            const domRect = listBoxRef.current!.getBoundingClientRect()
            const adjustedListBoxHeight = window.innerHeight - domRect.top
            if (adjustedListBoxHeight > 0) {
                setAdjustedListBoxHeight(adjustedListBoxHeight)
            }

            if (inputValue && inputValue.length >= minInputLength) {
                fetchHints(inputValue)
            } else {
                fetchHints('')
            }

            prevInputValueRef.current = inputValue ?? ''
        },
        onSelectedItemChange: ({ selectedItem }) => {
            if (selectedItem && onSelectHint) {
                // prevInputValueRef contains the actual text the user typed in before selecting
                // one of the Hints
                onSelectHint(selectedItem, prevInputValueRef.current)
            }
        },
    })

    const inputPadding =
        searchIconPosition === 'right' ? 'pr-8 pl-4' : 'pl-8 pr-4'

    useEffect(() => {
        listBoxRef.current!.style.maxHeight = adjustedListBoxHeight + 'px'
    }, [adjustedListBoxHeight, listBoxRef])

    const prevHints = usePrevious(hints)
    const isLoading = hints === prevHints

    const noSuggestions =
        !isLoading && !hints.length && inputValue.length >= minInputLength

    const inputProps = getInputProps({
        // Suppress aria-labelledby since we only have a `placeholder` attribute and
        // not a <label> element. For accessibility, we use `aria-label` below instead.
        'aria-labelledby': undefined,
        autoFocus,
        ref: inputRef,
        onFocus: () => {
            if (openMenuOnFocus) {
                openMenu()
            }
        },
    })

    const showHints =
        isOpen &&
        (hints.length > 0 || (headerHints?.length ?? 0) > 0) &&
        inputValue.length >= minInputLength

    const ulVisibilityClassName = !showHints ? 'hidden' : ''

    return (
        <div
            className={lit`${buildClassName(
                'AutoSuggest'
            )} relative bg-white text-black ${className}`}
        >
            <div>
                {searchIconPosition === 'left' ? (
                    <SearchIcon
                        size="medium"
                        className="absolute left-0 top-4"
                    />
                ) : (
                    inputAside
                )}
                <input
                    {...inputProps}
                    className={lit`${buildClassName(
                        'AutoSuggestInput'
                    )} h-14 w-full placeholder-shown:truncate focus:outline-none ${inputBorderClassName} ${inputPadding} ${inputClassName}`}
                    placeholder={placeholder}
                    aria-label={ariaLabel}
                />

                {searchIconPosition === 'right' ? (
                    <SearchIcon
                        size="medium"
                        className="absolute right-4 top-4"
                    />
                ) : (
                    inputAside
                )}

                <ul
                    {...getMenuProps({ ref: listBoxRef })}
                    className={lit`2xs-max:scrollbar-none absolute z-[900] mt-[1px] w-full overflow-auto bg-white ${listboxClassName} ${ulClassName} ${ulVisibilityClassName}`}
                    aria-labelledby={inputProps.id}
                    style={ulStyle}
                >
                    {isOpen &&
                        headerHints &&
                        headerHints.length > 0 &&
                        headerHints.map((hint, index) => {
                            const isSelected = highlightedIndex === index

                            const className = lit`w-full italic border border-y-transparent border-x-neutral-200 ${hintPaddingClassName}
                                ${isSelected && '!border !border-black'}`

                            return (
                                <li
                                    key={index}
                                    className={className}
                                    {...getItemProps({
                                        item: hint,
                                        index,
                                    })}
                                >
                                    {renderHint(hint)}
                                </li>
                            )
                        })}
                    {isOpen &&
                        hints.map((hint, index) => {
                            const headerHintsLength = headerHints?.length ?? 0
                            const logicalIndex = index + headerHintsLength
                            const isSelected = highlightedIndex === logicalIndex

                            const className = lit`
                                w-full border border-b-neutral-200 border-x-neutral-200 border-t-transparent ${hintPaddingClassName}
                                ${isSelected && '!border !border-black'}`

                            return (
                                <li
                                    key={logicalIndex}
                                    className={className}
                                    {...getItemProps({
                                        item: hint,
                                        index: logicalIndex,
                                    })}
                                >
                                    {renderHint(hint)}
                                </li>
                            )
                        })}
                    {noSuggestions && noResultsText && (
                        <li
                            className={`flex w-full justify-end border-b border-neutral-200 px-4 py-5 text-xs`}
                        >
                            {noResultsText}
                        </li>
                    )}
                </ul>
            </div>
        </div>
    )
}

function defaultRenderHintFunc(item: any) {
    return <>{item}</>
}
